import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useStore } from "@/stores/useStore";
import { useLayouts } from "@/hooks/useLayouts";
import { useContainerMetricsBoard } from "@/hooks/useContainerMetricsBoard";
import { useContainerMetricsTable } from "@/hooks/useContainerMetricsTable";
import { useContainerMetricsAnalytics } from "@/hooks/useContainerMetricsAnalytics";
import { LayoutSelector } from "@/components/Dashboard/LayoutSelector";
import { ContainerMetricsTable } from "@/components/ContainerMetrics/ContainerMetricsTable";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { useHeaderContext } from "@/hooks/useHeaderContext";
import { MetricsHeader } from "@/components/Page/Header/MetricsHeader";

const ContainerMetricsPage = () => {
  const shop = useStore((s) => s.shop);
  const setDashboardLayoutId = useStore((s) => s.setDashboardLayoutId);
  const shopId = shop?.id;

  const { logContainerMetricsViewed } = useContainerMetricsAnalytics();
  const hasLoggedRef = useRef(false);

  const { layouts } = useLayouts();
  const layoutNames = useMemo(
    () => layouts.map((layout) => Object.keys(layout)[0]),
    [layouts]
  );

  const { selectedBoard, selectBoard } = useContainerMetricsBoard(
    shopId,
    layoutNames
  );
  const rows = useContainerMetricsTable(selectedBoard);

  const { setHeaderContent } = useHeaderContext();

  useEffect(() => {
    setHeaderContent(shopId ? <MetricsHeader shopId={shopId} /> : null);
    return () => setHeaderContent(null);
  }, [setHeaderContent, shopId]);

  useEffect(() => {
    if (shopId && !hasLoggedRef.current) {
      hasLoggedRef.current = true;
      logContainerMetricsViewed();
    }
  }, [shopId, logContainerMetricsViewed]);

  const handleGoToBoard = () => {
    if (selectedBoard) {
      setDashboardLayoutId(selectedBoard);
    }
  };

  return (
    <div className="mx-auto w-full max-w-screen-lg space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Container Metrics</h1>
        <p className="mt-1 text-gray-400">
          Average time repair orders spend in each container.
        </p>
      </div>
      <div>
        <label className="text-sm font-medium text-gray-400">Board</label>
        <div className="mt-2 flex items-center gap-4">
          <LayoutSelector
            layouts={layoutNames}
            selectedLayout={selectedBoard}
            onLayoutChange={selectBoard}
          />
          {shopId && (
            <Link
              to={`/${shopId}`}
              onClick={handleGoToBoard}
              className="flex items-center gap-1 text-blue-400 hover:opacity-70"
            >
              Go to board
              <Icon name={IconNames.rightArrow} size={16} />
            </Link>
          )}
        </div>
      </div>
      <ContainerMetricsTable rows={rows} />
    </div>
  );
};

export default ContainerMetricsPage;
